import { useQuery } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { BRAND_BY_THEME, BRAND_FACE } from '@/shared/branding/brand'
import {
  readLoginPreferences,
  rememberLoginAppearance,
} from '@/shared/preferences/loginPreferences'
import { sessionQueryOptions } from '@/shared/session/session'
import { usePersistedUserSetting } from '@/shared/session/uiSettings'

import {
  decodeAppearancePayload,
  DEFAULT_APPEARANCE,
  encodeAppearancePayload,
  UI_SETTINGS_APPEARANCE_SCOPE,
  type Appearance,
} from './appearance'

/**
 * Holds the account's appearance and applies it to the document.
 *
 * THE DOCUMENT ELEMENT IS THE RENDER TARGET, FOR ALL FOUR AXES. Themes are CSS
 * attribute blocks (`:root[data-theme][data-mode]`, see `styles/tokens.css`)
 * and the two reading dials are two more of them (`styles/appearance.css`), so
 * applying an appearance means stamping what the stylesheet reads. Half a stamp
 * is never correct — `data-theme` alone would pin light mode, `data-mode` alone
 * would change nothing — so one effect owns all four rather than four effects
 * owning one each and running in some order.
 *
 * THERE IS ALWAYS AN APPEARANCE. An account that has not chosen renders
 * `DEFAULT_APPEARANCE`, stamped exactly like a chosen one — the unchosen state
 * is a theme the product picked, not a gap the stylesheet fills from the
 * operating system. The document already arrives carrying that pair from
 * `index.html`, so what this effect is FOR is the replacement: a stored choice
 * arriving from the account, or a new one made in this session.
 *
 * PERSISTENCE HAS TWO SUBJECTS. The provider wraps the router, so it is alive
 * on the sign-in screen too — where there is no account to store into. There a
 * choice goes only to the validated browser login hint. Once a session is
 * known, the account UI-settings row is the subject instead and replaces the
 * hint. The session query here is subscribe-only (`enabled: false`): it decides
 * which subject exists but never becomes the thing that asks, because on the
 * sign-in screen `/auth/me` is a guaranteed 401.
 *
 */
interface AppearanceContextValue {
  /** The appearance in force for the public login or current account. */
  appearance: Appearance
  setAppearance: (next: Appearance) => void
  /** Enter the browser-owned public-login scope and restore its hint. */
  applyLoginAppearance: () => void
  /** Leave the public-login scope so an authenticated account can take over. */
  releaseLoginAppearance: () => void
}

const AppearanceContext = createContext<AppearanceContextValue | null>(null)

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const session = useQuery({ ...sessionQueryOptions(), enabled: false })
  const [loginAppearance, setLoginAppearance] = useState(
    () => readLoginPreferences().appearance ?? DEFAULT_APPEARANCE,
  )
  // Absence of session data does not imply that the public login is rendering:
  // an authenticated cold load begins in that same unresolved state. Only
  // LoginPage may enter this scope, in its pre-paint layout effect.
  const [loginActive, setLoginActive] = useState(false)
  const { value, setValue } = usePersistedUserSetting<Appearance | null>({
    scope: UI_SETTINGS_APPEARANCE_SCOPE,
    scopeKey: null,
    defaultValue: null,
    enabled: session.data !== undefined,
    encode: encodeAppearancePayload,
    decode: decodeAppearancePayload,
  })

  // `value` stays nullable below this line: `null` means NOTHING IS STORED,
  // which is what decides whether a save is owed, and it must not be confused
  // with the default that is rendered in its place.
  const applied = loginActive ? loginAppearance : value ?? DEFAULT_APPEARANCE

  useLayoutEffect(() => {
    const root = document.documentElement
    root.dataset['theme'] = applied.theme
    root.dataset['mode'] = applied.mode
    // Stamped even at their defaults, which define no rules: the attribute is
    // then simply what the document says about itself, and a test — or a person
    // with the inspector open — reads the dial in force rather than inferring
    // it from an absence.
    root.dataset['scale'] = String(applied.scale)
    root.dataset['density'] = applied.density
  }, [applied.theme, applied.mode, applied.scale, applied.density])

  /*
   * THE TAB'S ICON FOLLOWS THE THEME, for the same reason the tokens do: the
   * tab is part of the interface, and a white-label still wearing the host's
   * mark is a white-label with a leak in the one place a clinician keeps
   * twenty of open. `brand.ts` decides whose face it is; this only applies it.
   *
   * THE TAB'S WORDS ARE NOT WRITTEN HERE, and were until #267. They have to
   * name the page as well as the brand, and which page this is belongs to the
   * router rather than to a theme: `app/documentTitle.tsx` composes the two
   * out of the SAME `brand.ts` face, so a white-label's tab reads its own name
   * without this effect having to know what a route is.
   *
   * Keyed on the theme ALONE, unlike the stamp above: the mode, the scale and
   * the density have nothing to say about whose product this is, and rewriting
   * the tab on a density change would replace the icon element for nothing.
   *
   * The icon is REPLACED rather than mutated, because a browser that has
   * already fetched an icon does not re-read an href it is still holding; a
   * fresh element is the reliable way to ask for a different picture. If the
   * document ships without one — nothing does, `index.html` carries the pair —
   * the link is created, which is also what makes this safe in a test document.
   */
  useEffect(() => {
    const face = BRAND_FACE[BRAND_BY_THEME[applied.theme]]
    const existing = document.head.querySelector<HTMLLinkElement>("link[rel='icon']")
    const link = document.createElement('link')
    link.rel = 'icon'
    link.type = 'image/png'
    link.href = face.faviconUrl
    existing?.remove()
    document.head.append(link)
  }, [applied.theme])

  const applyLoginAppearance = useCallback(() => {
    setLoginActive(true)
    setLoginAppearance(readLoginPreferences().appearance ?? DEFAULT_APPEARANCE)
  }, [])
  const releaseLoginAppearance = useCallback(() => {
    setLoginActive(false)
  }, [])

  const setAppearance = useCallback(
    (next: Appearance) => {
      if (loginActive) {
        setLoginAppearance(next)
        rememberLoginAppearance(next)
        return
      }
      setValue(next)
    },
    [loginActive, setValue],
  )

  const context = useMemo<AppearanceContextValue>(
    () => ({
      appearance: applied,
      setAppearance,
      applyLoginAppearance,
      releaseLoginAppearance,
    }),
    [applied, setAppearance, applyLoginAppearance, releaseLoginAppearance],
  )

  return <AppearanceContext.Provider value={context}>{children}</AppearanceContext.Provider>
}

export function useAppearance(): AppearanceContextValue {
  const context = useContext(AppearanceContext)
  if (!context) {
    throw new Error('useAppearance requires an AppearanceProvider')
  }
  return context
}
